import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getServiceRequestSubscribers,
  saveServiceRequestSubscribers,
} from 'src/services/client';

export interface Subscriber {
  // Row id of the subscription record. NOT the userId — these are distinct.
  // Stable across unsubscribe → resubscribe cycles (server soft-deletes + undeletes).
  id: string;
  serviceRequestId: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  createdOn: string;
}

interface ApiResponseEnvelope<TData = any> {
  statusCode: number;
  message: string;
  data: TData;
  warnings: string[] | null;
}

export interface SaveSubscribersResult {
  ok: boolean;
  errorMessage?: string;
}

export interface UseServiceRequestSubscribersReturn {
  subscribers: Subscriber[];
  loading: boolean;
  saving: boolean;
  loadSubscribers: () => Promise<void>;
  saveSubscribers: (userIds: string[]) => Promise<SaveSubscribersResult>;
}

const GENERIC_SAVE_ERROR_MESSAGE: string =
  'Something went wrong while updating subscribers. Please try again.';

const GENERIC_LOAD_ERROR_MESSAGE: string =
  'Could not load subscribers for this service request.';

export const useServiceRequestSubscribers = (
  serviceRequestId: string
): UseServiceRequestSubscribersReturn => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const loadSubscribers = async (): Promise<void> => {
    if (!serviceRequestId) {
      return;
    }

    setLoading(true);

    try {
      const response: any = await getServiceRequestSubscribers(serviceRequestId);

      if (response.ok || response.status === 200) {
        const envelope: ApiResponseEnvelope<Subscriber[]> = await response.json();
        setSubscribers(envelope.data ?? []);
        return;
      }

      throw new Error('Failed to load subscribers');
    } catch (error: any) {
      console.error('Error loading subscribers:', error);
      toast.error('🚫 ' + GENERIC_LOAD_ERROR_MESSAGE, {
        autoClose: 8000,
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSubscribers = async (
    userIds: string[]
  ): Promise<SaveSubscribersResult> => {
    if (!serviceRequestId) {
      return { ok: false, errorMessage: GENERIC_SAVE_ERROR_MESSAGE };
    }

    setSaving(true);

    try {
      const response: any = await saveServiceRequestSubscribers(
        serviceRequestId,
        { userIds }
      );

      if (response.ok || response.status === 200) {
        const envelope: ApiResponseEnvelope<Subscriber[]> = await response.json();
        setSubscribers(envelope.data ?? []);

        toast.success('ⓘ Subscribers have been updated.', {
          autoClose: 5000,
          position: 'top-center',
        });

        return { ok: true };
      }

      const errorEnvelope: ApiResponseEnvelope = await response.json();
      const errorMessage: string =
        errorEnvelope.message || GENERIC_SAVE_ERROR_MESSAGE;

      toast.error('🚫 ' + errorMessage, {
        autoClose: 8000,
        position: 'top-center',
      });

      return { ok: false, errorMessage };
    } catch (error: any) {
      console.error('Error saving subscribers:', error);

      toast.error('🚫 ' + GENERIC_SAVE_ERROR_MESSAGE, {
        autoClose: 8000,
        position: 'top-center',
      });

      return { ok: false, errorMessage: GENERIC_SAVE_ERROR_MESSAGE };
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadSubscribers();
  }, [serviceRequestId]);

  return {
    subscribers,
    loading,
    saving,
    loadSubscribers,
    saveSubscribers,
  };
};